import React, { useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { isqUnselectOption } from "@learningblocks/simulations/shared-components/lib/Questions/ImageSelectionQuestionRedux/store/actions";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import { FormattedMessage } from "@learningblocks/shared-components/lib/FormattedMessage";
import { GridItem } from "@learningblocks/interactives/interactive-infographic-components/lib/GridItem";
import { AnimationEntity } from "@learningblocks/interactives/interactive-infographic-components/lib/AnimationEntity";
import { ImageEntity } from "@learningblocks/interactives/interactive-infographic-components/lib/ImageEntity";
import { NotebookEntity } from "@learningblocks/interactives/interactive-infographic-components/lib/NotebookEntity";
import { Hotspot } from "@learningblocks/interactives/interactive-infographic-components/lib/Hotspot";
import { WidgetWrapper } from "@learningblocks/interactives/interactive-infographic-components/lib/WidgetWrapper";
import { IntroModal } from "@learningblocks/interactives/interactive-infographic-components/lib/IntroModal";
import { RotateOverlay } from "@learningblocks/interactives/interactive-infographic-components/lib/RotateOverlay";
import { AnimationLegend } from "@learningblocks/interactives/interactive-infographic-components/lib/AnimationLegend";
import { DevMenu } from "@learningblocks/interactives/interactive-infographic-components/lib/DevMenu";
import {
  generateAnimationStateColors,
  getActiveAnimations,
} from "@learningblocks/interactives/interactive-infographic-components/lib/utils/animationColorMapping";

import className from "../style.module.scss";
import { Button } from "@labxchange/buttons";

export function ImageDiscoveryCanvas(props) {
  const intl = useIntl();
  const dispatch = props.requiresRedux ? useDispatch() : null;

  const baseUrl =
    window.CONTENT_BASE_URL ||
    window.location.origin + window.location.pathname;

  const playedStates = useRef(new Set()); // Persist animation play states across renders

  const canvasContainerRef = useRef(null);
  const containerRef = useRef(null);
  const [cellSize, setCellSize] = useState(0);
  const [gridSize, setGridSize] = useState({ x: 0, y: 0 });
  const [maxViewX, setMaxViewX] = useState(0);
  const [maxViewY, setMaxViewY] = useState(0);
  const [currentView, setCurrentView] = useState(
    props.sceneData.currentView ?? props.sceneData.mainView
  );
  const [grid, setGrid] = useState([]);
  const [showGrid, setShowGrid] = useState(false);
  const [devView, setDevView] = useState(false);
  const [showAnimationHighlight, setShowAnimationHighlight] = useState(false);
  const [hiddenAnimations, setHiddenAnimations] = useState(new Set());
  const [hiddenAnimationContent, setHiddenAnimationContent] = useState(
    new Set()
  );
  const [showHotspots] = useState(true);
  const [device, setDevice] = useState("mob"); // "mob", "tab", "dsk"
  let timer;
  const [tick, setTick] = useState(0);
  const [closeWidget, setCloseWidget] = useState(false);
  const [introModal, setIntroModal] = useState(props.sceneData.intro !== "");

  const [widgetData, setWidgetData] = useState({
    type: "none",
    active: false,
    hotspotID: 0,
  });
  const [views, setViews] = useState({ ...props.sceneData.views });
  const [hotspots, setHotspots] = useState({ ...props.sceneData.hotspots });
  const entities = props.sceneData.entities;
  const [resources, setResources] = useState({});
  const [triggerLog, setTriggerLog] = useState(
    props.sceneData.triggerLog || []
  );
  const [latestHotspotRef, setLatestHotspotRef] = useState(0);

  const [dev] = useState(props.sceneData.debug);
  const [loaded, setLoaded] = useState(false);
  const preview = props.preview;

  const [showOrientationPrompt, setShowOrientationPrompt] = useState(false);
  const [orientationPromptDismissed, setOrientationPromptDismissed] = useState(
    () => sessionStorage.getItem("orientationPromptDismissed") === "true"
  );

  // Maintain hotspot order if provided (to assist with navigation order)
  const hotspotOrder =
    props.sceneData.hotspotOrder || Object.keys(props.sceneData.hotspots);

  useEffect(() => {
    setGridLimits();
    checkDevice();
    if (props.standalone) {
      document.title = `LabXchange | ${intl.formatMessage({
        id: props.sceneData.title,
        defaultMessage: props.sceneData.title,
        description: "",
      })}`;
    }
    if (props.forceStateChangers) {
      forceStateChangers(props.forceStateChangers);
    }
  }, []);

  useEffect(() => {
    const updateCellSize = () => {
      if (containerRef.current && gridSize.x > 0 && maxViewY > 0) {
        const containerHeight = containerRef.current.offsetHeight;
        let newCellHeight;
        if (devView) {
          newCellHeight = containerHeight / maxViewY / 2;
        } else {
          newCellHeight = containerHeight / views[currentView].size.height;
        }
        setCellSize(newCellHeight);
      }
    };

    updateCellSize();

    window.addEventListener("resize", updateCellSize);
    return () => window.removeEventListener("resize", updateCellSize);
  }, [gridSize, maxViewY, currentView, devView]);

  useEffect(() => {
    preloadResources(intl.locale);
  }, [intl.locale]);

  useEffect(() => {
    updateTick();
    return () => clearInterval(timer);
  }, [tick]);

  useEffect(() => {
    setTick(0);
    trackProgress();
  }, [currentView, hotspots, triggerLog]);

  // Sync hotspot images from props to local state whenever props.sceneData.hotspots changes
  useEffect(() => {
    const updatedHotspots = { ...hotspots };
    let hasChanges = false;

    Object.keys(props.sceneData.hotspots).forEach((hotspotID) => {
      // Check if image in props exists or not
      const propsHasImage = props.sceneData.hotspots[hotspotID]?.props?.image;
      const stateHasImage = updatedHotspots[hotspotID]?.props?.image;
      if (!propsHasImage && stateHasImage) {
        if (updatedHotspots[hotspotID]?.props) {
          // Remove the image property from props
          const { image, ...restProps } = updatedHotspots[hotspotID].props;
          void image;
          updatedHotspots[hotspotID] = {
            ...updatedHotspots[hotspotID],
            props: { ...restProps },
          };
          hasChanges = true;
        }
      }
    });
    if (hasChanges) {
      setHotspots(updatedHotspots);
    }
  }, [props.sceneData.hotspots]);

  useEffect(() => {
    setTick(0);
    if (showGrid) {
      doTheGriddy();
    }
  }, [currentView, gridSize, showGrid]);

  useEffect(() => {
    const handleResize = () => {
      const isPortraitOrTooSmall =
        window.innerWidth < 600 && window.innerHeight > window.innerWidth;

      if (isPortraitOrTooSmall && !orientationPromptDismissed) {
        setShowOrientationPrompt(true);
      } else {
        setShowOrientationPrompt(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [orientationPromptDismissed]);

  useEffect(() => {
    if (device === "mob") {
      // let elem = document.getElementById(`canvas_${props.sceneData.id}`);
      // elem.requestFullscreen()
      //   .then(()=>{
      //     screen.orientation.lock("landscape-primary")
      //       .then(() => {
      //         console.log("Orientation locked to horizontal.")
      //       })
      //       .catch((error) => {
      //         console.log(error)
      //       });
      //   })
      //   .catch((err) => {
      //     console.log(
      //       err
      //     );
      //   }
      // );
    }
  }, [device]);

  useEffect(() => {
    if (props.handleSceneUpdate) {
      props.handleSceneUpdate(hotspots, triggerLog, currentView);
    }
  }, [hotspots, triggerLog, currentView]);

  // Watch for MSCQ questions becoming complete and trigger hotspots
  useEffect(() => {
    Object.keys(hotspots).forEach((hotspotID) => {
      const hotspot = hotspots[hotspotID];
      // Check if this is an MSCQ that just became complete and hasn't been triggered yet
      if (
        hotspot.props?.type === "MSCQ" &&
        hotspot.props?.complete === true &&
        !hotspot.triggered &&
        !hotspot.preventSelfCompletion
      ) {
        // Trigger this hotspot
        const newHotspots = { ...hotspots };
        newHotspots[hotspotID] = {
          ...newHotspots[hotspotID],
          triggered: true,
        };
        setHotspots(newHotspots);
      }
    });
  }, [hotspots]);

  // Check triggerLog with support for negation (! prefix)
  const isTriggerActive = (triggerLog, triggerName) => {
    if (typeof triggerName === "string" && triggerName.startsWith("!")) {
      return !triggerLog.includes(triggerName.slice(1));
    } else {
      return triggerLog.includes(triggerName);
    }
  };

  const loadImage = (src, lng) => {
    return new Promise((resolve, reject) => {
      const image = new Image();
      const imageUrl = src.includes(window.location.origin)
        ? src
        : `${baseUrl}locales/${lng}/images/${src}`;
      image.src = imageUrl;
      image.onload = () => {
        resolve({ src: src, resources: image, lng: lng });
      };
      image.onerror = () => {
        const fallbackImageUrl = src.includes(window.location.origin)
          ? src
          : `${baseUrl}locales/en/images/${src}`;
        image.src = fallbackImageUrl;
        image.onload = () => {
          resolve({ src: src, resources: image, lng: "en" });
        };
        image.onerror = () => {
          reject(new Error(`Failed to load ${fallbackImageUrl}`));
        };
      };
    });
  };

  const preloadResources = (lng) => {
    const updatedResources = resources;

    const promises_entities = props.sceneData.entities.reduce((acc, entity) => {
      if (entity.type === "image") {
        entity.states.forEach((data) => {
          acc.push(loadImage(data.props.src, lng));
        });
      }
      return acc;
    }, []);

    const promises_widgets = Object.values(props.sceneData.hotspots).reduce(
      (acc, hotspot) => {
        const type = hotspot.props.type;
        const validTypes = ["learn-more", "CSCQ", "MSCQ", "TABLE_QUESTION"];

        if (validTypes.includes(type)) {
          const imgSrc = hotspot?.props?.image?.src || "";
          if (imgSrc !== "") {
            acc.push(loadImage(imgSrc, lng));
          }
        }

        return acc;
      },
      []
    );

    Promise.allSettled([...promises_entities, ...promises_widgets])
      .then((results) => {
        results.forEach((result) => {
          if (result.status === "fulfilled") {
            const { src, resources } = result.value;
            updatedResources[`${src}_${lng}`] = resources;
          }
        });
        setResources(updatedResources);
        setLoaded(true);
      })
      .catch((error) => {
        console.error("Error preloading resource:", error);
      });
  };

  const updateTick = () => {
    timer =
      !timer &&
      setInterval(() => {
        setTick((prevTick) => prevTick + 1);
      }, 10000);
  };

  const checkDevice = () => {
    const viewWidth = screen.width;
    if (viewWidth > 1024) {
      setDevice("dsk");
    } else if (viewWidth <= 1024 && viewWidth > 720) {
      setDevice("tab");
    } else {
      setDevice("mob");
    }
  };

  const setGridLimits = () => {
    let x_max = 0;
    let y_max = 0;
    Object.keys(views).map((viewID) => {
      const view = views[viewID];
      if (view.size.height > y_max) {
        y_max = view.size.height;
      }
      if (view.size.width > x_max) {
        x_max = view.size.width;
      }
    });
    setMaxViewX(x_max);
    setMaxViewY(y_max);
    setGridSize({ x: x_max * Object.keys(views).length, y: y_max });
  };

  const trackProgress = () => {
    let maxActions = 0;
    let triggeredActions = 0;
    let triggeredActionsOverall = 0;
    Object.keys(hotspots).map((hotspotID) => {
      const hotspot = hotspots[hotspotID];
      if (
        (props.trackProgress &&
          hotspot.triggered &&
          hotspot.postTrigger !== "no-change") ||
        (hotspot.props.complete && hotspot.postTrigger !== "no-change")
      ) {
        triggeredActionsOverall++;
      }
      if (
        hotspot.visibility_dependencies.views.includes(currentView) ||
        hotspot.visibility_dependencies.views.length === 0
      ) {
        if (hotspot.postTrigger !== "no-change") {
          maxActions++;
          if (hotspot.triggered) {
            triggeredActions++;
          }
        }
      }
    });
    if (props.trackProgress) {
      props.updateProgress(triggeredActionsOverall);
    }
    if (triggeredActions === maxActions) {
      const newViews = { ...views };

      if (currentView === props.sceneData.mainView) {
        let complete = true;
        Object.keys(views).map((viewID, key) => {
          const view = views[viewID];
          if (key !== 0 && !view.complete) {
            complete = false;
          }
        });
        if (complete) {
          newViews[currentView].complete = true;
          setViews({ ...newViews });
        }
      } else {
        newViews[currentView].complete = true;
        setViews({ ...newViews });
      }
    }
  };

  const checkEntityState = (entity) => {
    let activeState = null;

    // Iterate through each defined state until hase dependencies that are met
    entity.states.forEach((state, key) => {
      if (activeState !== null) return; // previous state conditions met

      const {
        views = [],
        hotspots: hsDeps = [],
        customTriggers = [],
      } = state.state_dependencies;

      // 1) View dependency
      if (views.length > 0 && !views.includes(currentView)) {
        return;
      }

      // 2) Hotspot-based dependencies
      if (hsDeps.length > 0) {
        const allHotspotsMet = hsDeps.every((dep) => {
          const target = hotspots[dep.id];
          if (!target) return false;

          return Object.entries(dep.requirement).every(
            ([prop, expectedVal]) => {
              const actual =
                prop === "props" ? target.props.currentState : target[prop];
              const expected =
                prop === "props" ? expectedVal.currentState : expectedVal;
              return actual === expected;
            }
          );
        });

        if (!allHotspotsMet) {
          return;
        }
      }

      // 3) Custom-trigger dependencies
      if (customTriggers.length > 0) {
        const allFired = customTriggers.every((name) =>
          isTriggerActive(triggerLog, name)
        );
        if (!allFired) {
          return;
        }
      }

      // All checks passed - active state
      activeState = key;
    });

    return activeState;
  };

  const animationColorMap = React.useMemo(() => {
    return generateAnimationStateColors(entities);
  }, [entities]);

  const activeAnimations = React.useMemo(() => {
    return getActiveAnimations(entities, checkEntityState);
  }, [entities, currentView, hotspots, triggerLog]);

  const toggleAnimationHighlight = (animId, stateIndex) => {
    const colorKey = `${animId}-${stateIndex}`;
    setHiddenAnimations((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(colorKey)) {
        newSet.delete(colorKey);
      } else {
        newSet.add(colorKey);
      }
      return newSet;
    });
  };

  const toggleAnimationContent = (animId, stateIndex) => {
    const colorKey = `${animId}-${stateIndex}`;
    setHiddenAnimationContent((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(colorKey)) {
        newSet.delete(colorKey);
      } else {
        newSet.add(colorKey);
      }
      return newSet;
    });
  };

  const doTheGriddy = () => {
    const griddy = [];
    for (let y = 0; y < views[currentView].size.height; y++) {
      for (let x = 0; x < views[currentView].size.width; x++) {
        griddy.push({
          x: x + views[currentView].x_offset,
          y: y + views[currentView].y_offset,
        });
      }
    }
    setGrid(griddy);
  };

  const checkHotspotVisibilityDependencies = (hotspot) => {
    let visible = true;

    if (
      !hotspot.visibility_dependencies.views.includes(currentView) &&
      hotspot.visibility_dependencies.views.length !== 0
    ) {
      visible = false;
    }

    if (hotspot.triggered && hotspot.postTrigger === "hidden") {
      visible = false;
    }

    for (let i = 0; i < hotspot.visibility_dependencies.hotspots.length; i++) {
      for (const property in hotspot.visibility_dependencies.hotspots[i]
        .requirement) {
        if (property !== "props") {
          if (
            hotspots[hotspot.visibility_dependencies.hotspots[i].id][
              property
            ] !==
            hotspot.visibility_dependencies.hotspots[i].requirement[property]
          ) {
            visible = false;
          }
        } else {
          if (
            hotspots[hotspot.visibility_dependencies.hotspots[i].id][property]
              .currentState !==
            hotspot.visibility_dependencies.hotspots[i].requirement[property]
              .currentState
          ) {
            visible = false;
          }
        }
      }
    }

    if (Object.hasOwn(hotspot.visibility_dependencies, "customTriggers")) {
      let customTriggerPass = true;
      hotspot.visibility_dependencies.customTriggers.map((triggerName) => {
        if (!isTriggerActive(triggerLog, triggerName)) {
          customTriggerPass = false;
        }
      });
      if (!customTriggerPass) {
        visible = false;
      }
    }

    return visible;
  };

  const checkHotspotHiddenDependencies = (hotspot) => {
    let visible = true;

    if (
      !hotspot.hidden_dependencies.views.includes(currentView) &&
      hotspot.hidden_dependencies.views.length !== 0
    ) {
      visible = false;
    }

    if (hotspot.triggered && hotspot.postTrigger === "hidden") {
      visible = false;
    }

    const visibilityArr = [];
    for (let i = 0; i < hotspot.hidden_dependencies.hotspots.length; i++) {
      for (const property in hotspot.hidden_dependencies.hotspots[i]
        .requirement) {
        if (property !== "props") {
          if (
            hotspots[hotspot.hidden_dependencies.hotspots[i].id][property] ===
            hotspot.hidden_dependencies.hotspots[i].requirement[property]
          ) {
            visibilityArr.push(true);
          } else {
            visibilityArr.push(false);
          }
        } else {
          if (
            hotspots[hotspot.hidden_dependencies.hotspots[i].id][property]
              .currentState ===
            hotspot.hidden_dependencies.hotspots[i].requirement[property]
              .currentState
          ) {
            visibilityArr.push(true);
          } else {
            visibilityArr.push(false);
          }
        }
      }
    }

    if (visibilityArr.length > 0) {
      visible = visibilityArr.includes(false);
    }

    if (Object.hasOwn(hotspot.hidden_dependencies, "customTriggers")) {
      let customTriggerPass = false;
      hotspot.hidden_dependencies.customTriggers.map((triggerName) => {
        if (isTriggerActive(triggerLog, triggerName)) {
          customTriggerPass = true;
        }
      });
      if (customTriggerPass) {
        visible = false;
      }
    }

    return visible;
  };

  const checkHotspotLockedDependencies = (hotspot) => {
    let locked = false;

    if (hotspot.triggered && hotspot.postTrigger === "hidden") {
      locked = true;
    }

    for (let i = 0; i < hotspot.locked_dependencies.hotspots.length; i++) {
      for (const property in hotspot.locked_dependencies.hotspots[i]
        .requirement) {
        if (property !== "props") {
          if (
            hotspots[hotspot.locked_dependencies.hotspots[i].id][property] !==
            hotspot.locked_dependencies.hotspots[i].requirement[property]
          ) {
            locked = true;
          }
        } else {
          if (
            hotspots[hotspot.locked_dependencies.hotspots[i].id][property]
              .currentState !==
            hotspot.locked_dependencies.hotspots[i].requirement[property]
              .currentState
          ) {
            locked = true;
          }
        }
      }
    }

    if (Object.hasOwn(hotspot.locked_dependencies, "customTriggers")) {
      let customTriggerPass = true;
      hotspot.locked_dependencies.customTriggers.map((triggerName) => {
        if (!isTriggerActive(triggerLog, triggerName)) {
          customTriggerPass = false;
        }
      });
      if (!customTriggerPass) {
        locked = true;
      }
    }

    return locked;
  };

  const checkCompleteDependencies = (id) => {
    const hs = hotspots[id];
    if (!hs) return;

    // get complete_dependencies
    const cd = hs.complete_dependencies || {};
    const {
      views: cdViews = [],
      hotspots: cdHotspots = [],
      customTriggers: cdTriggers = [],
    } = cd;

    // if no complete deps at all, return
    if (
      cdViews.length === 0 &&
      cdHotspots.length === 0 &&
      cdTriggers.length === 0
    ) {
      return;
    }

    // 1) view-based dependency
    if (cdViews.length > 0 && !cdViews.includes(currentView)) {
      return;
    }

    // 2) hotspot-based complete_dependencies
    for (const { id: depID, requirement } of cdHotspots) {
      const target = hotspots[depID];
      if (!target) return;

      for (const prop in requirement) {
        const actual =
          prop === "props" ? target.props.currentState : target[prop];
        const expected =
          prop === "props" ? requirement.props.currentState : requirement[prop];

        if (actual !== expected) {
          return;
        }
      }
    }

    // 3) customTriggers
    if (
      cdTriggers.length > 0 &&
      !cdTriggers.every((name) => isTriggerActive(triggerLog, name))
    ) {
      return;
    }

    // All complete-deps passed - mark it triggered
    setHotspots((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        triggered: true,
      },
    }));
  };

  const changeView = (view) => {
    if (view === currentView) {
      setCurrentView(props.sceneData.mainView);
    } else {
      setCurrentView(view);
    }
  };

  const changeState = (id, newIndex) => {
    const newHotspots = { ...hotspots };
    if (typeof newIndex === "number") {
      newHotspots[id].props.currentState = newIndex;
    } else {
      if (newHotspots[id].props.currentState === newHotspots[id].props.states) {
        newHotspots[id].props.currentState = 1;
      } else {
        newHotspots[id].props.currentState++;
      }
    }
    setHotspots({ ...newHotspots });
  };

  const nextHotspotRef = useRef(null);

  const handleTriggerContentBox = (id) => {
    if (props.requiresRedux && dispatch) {
      const newHotspots = props.sceneData.hotspots;
      if (newHotspots[id].isCorrect === false) {
        dispatch(
          isqUnselectOption({
            hotspotId: id,
            componentID: props.componentID,
          })
        );
      }
    } else {
      console.warn("Redux is not enabled. Skipping dispatch.");
    }
  };

  const forceStateChangers = (stateChangers) => {
    if (!Array.isArray(stateChangers) || stateChangers.length === 0) return;
    const newHotspots = { ...hotspots };
    stateChangers.forEach(({ id, state }) => {
      if (newHotspots[id]?.props?.type === "state-changer") {
        newHotspots[id] = {
          ...newHotspots[id],
          props: {
            ...newHotspots[id].props,
            currentState: state,
          },
        };
        setHotspots(newHotspots);
      }
    });
  };

  const handleTrigger = (id) => {
    if (hotspots[id].preventSelfCompletion === true) {
      return; // This hotspot is only completed through dependencies
    }

    // Create a deep copy of the hotspots state
    const newHotspots = { ...hotspots };

    if (newHotspots[id].props.type === "content-box") {
      handleTriggerContentBox(id);
    } else {
      newHotspots[id] = {
        ...newHotspots[id],
        triggered: true, // Ensure immutability
      };
      setHotspots(newHotspots);
    }

    if (newHotspots[id].postTrigger === "hidden" && hotspotOrder) {
      setTimeout(() => {
        const currentIndex = hotspotOrder.indexOf(id);
        if (currentIndex !== -1 && currentIndex < hotspotOrder.length - 1) {
          const nextHotspotId = hotspotOrder[currentIndex + 1];
          const nextHotspotElement = document.getElementById(
            `hotspotBtn_${nextHotspotId}`
          );
          if (nextHotspotElement) {
            nextHotspotRef.current = nextHotspotElement;
          } else {
            nextHotspotRef.current = null;
          }
        }
      }, 300);
    }
  };

  const updateWidgetData = (bool, type, hotspotID) => {
    const newWidgetData = { ...widgetData };
    newWidgetData.type = type;
    newWidgetData.hotspotID = hotspotID;
    newWidgetData.active = bool;

    setWidgetData({ ...newWidgetData });
  };

  const handleCloseWidget = () => {
    setCloseWidget(true);
    setTimeout(() => {
      setCloseWidget(false);
      updateWidgetData(false, "none", 0);
      if (nextHotspotRef?.current) {
        nextHotspotRef.current.focus();
      }
      if (latestHotspotRef?.current) {
        latestHotspotRef.current.focus();
      }
    }, 300);
  };

  const handleCSCQ_OptionSelect = (hotspotID, optionData) => {
    const newHotspots = { ...hotspots };
    const options = newHotspots[hotspotID].props.options;
    options.map((option) => {
      if (option.id === optionData.optionID) {
        if (option.selected) {
          option.selected = false;
        } else {
          option.selected = true;
        }
      } else {
        option.selected = false;
      }
    });
    setHotspots({ ...newHotspots });
  };

  const handleCSCQ_Submit = (hotspotID) => {
    const newHotspots = { ...hotspots };
    const widget = newHotspots[hotspotID].props;
    const options = widget.options;
    options.map((option) => {
      if (option.selected) {
        option.disabled = true;
        if (widget.answerId.includes(option.id)) {
          widget.complete = true;
        } else {
          if (widget.totalAttempts > 0) {
            widget.totalAttempts--;
            if (widget.totalAttempts === 0) {
              widget.complete = true;
            }
          }
        }
      }
    });
    setHotspots({ ...newHotspots });
  };

  const handleTableQuestion_Submit = (hotspotID) => {
    const newHotspots = { ...hotspots };
    const widget = newHotspots[hotspotID].props;
    if (!widget.complete) {
      widget.complete = true;
      setHotspots({ ...newHotspots });
    }
  };

  const handleMSCQRedux_OptionSelect = (hotspotID, checkboxData) => {
    const newHotspots = { ...hotspots };
    const widget = newHotspots[hotspotID].props;

    // Check if we're in "Try again" mode (has disabled options)
    const hasDisabledOptions = widget.checkboxes.some(
      (checkbox) => checkbox.disabled
    );

    // Update the checkbox element inside widget.checkboxes if id matches
    const updatedCheckboxes = widget.checkboxes.map((checkbox) => {
      // If in "Try again" mode, clear all disabled states (sync with Redux enableAllOptions)
      const updatedCheckbox = hasDisabledOptions
        ? { ...checkbox, disabled: false }
        : checkbox;

      if (checkbox.id === checkboxData.optionID) {
        return {
          ...updatedCheckbox,
          selected: checkboxData.checked,
        };
      }
      return updatedCheckbox;
    });

    // Create new objects all the way down to ensure React detects the change
    newHotspots[hotspotID] = {
      ...newHotspots[hotspotID],
      props: {
        ...newHotspots[hotspotID].props,
        checkboxes: updatedCheckboxes,
      },
    };

    setHotspots(newHotspots);
  };

  const handleMSCQRedux_Submit = (hotspotID, isComplete) => {
    // Update the local hotspots state with the completion status from Redux
    const newHotspots = { ...hotspots };
    newHotspots[hotspotID] = {
      ...newHotspots[hotspotID],
      props: {
        ...newHotspots[hotspotID].props,
        complete: isComplete,
      },
    };
    setHotspots(newHotspots);
    // The useEffect watching hotspots will trigger the hotspot when complete becomes true
  };

  const handleMSCQ_OptionSelect = (hotspotID, checkboxData) => {
    const newHotspots = { ...hotspots };

    // Create new objects all the way down to ensure React detects the change
    newHotspots[hotspotID] = {
      ...newHotspots[hotspotID],
      props: {
        ...newHotspots[hotspotID].props,
        checkboxes: newHotspots[hotspotID].props.checkboxes.map((checkbox) => {
          if (checkbox.id === checkboxData.optionID) {
            return {
              ...checkbox,
              selected: checkboxData.checked,
            };
          }
          return checkbox;
        }),
      },
    };

    setHotspots(newHotspots);
  };

  const handleMSCQ_Submit = (hotspotID) => {
    const newHotspots = { ...hotspots };
    const widget = newHotspots[hotspotID].props;
    const checkboxes = widget.checkboxes;

    const goal = widget.answerId.length;
    let score = 0;

    // Create new checkboxes array with updated disabled states
    const updatedCheckboxes = checkboxes.map((checkbox) => {
      if (checkbox.selected) {
        // Only count score for CORRECT answers
        if (widget.answerId.includes(checkbox.id)) {
          score++;
        }
        return {
          ...checkbox,
          disabled: true,
        };
      }
      return checkbox;
    });

    // Determine completion status
    let isComplete = false;
    let newTotalAttempts = widget.totalAttempts;

    if (score === goal) {
      isComplete = true;
    } else {
      // Only decrement attempts if showAttempts is true (when hidden, attempts are unlimited)
      if (widget.showAttempts && widget.totalAttempts > 0) {
        newTotalAttempts--;
        if (newTotalAttempts === 0) {
          isComplete = true;
        }
      }
    }

    // Track if this is a transition from incomplete to complete
    const wasIncomplete = !widget.complete;

    // Create new objects all the way down to ensure React detects the change
    newHotspots[hotspotID] = {
      ...newHotspots[hotspotID],
      props: {
        ...newHotspots[hotspotID].props,
        checkboxes: updatedCheckboxes,
        complete: isComplete,
        totalAttempts: newTotalAttempts,
      },
    };

    // Trigger hotspot immediately when first completed (update triggered state in same operation)
    if (
      isComplete &&
      wasIncomplete &&
      !hotspots[hotspotID].preventSelfCompletion
    ) {
      newHotspots[hotspotID] = {
        ...newHotspots[hotspotID],
        triggered: true,
      };
    }

    setHotspots(newHotspots);
  };

  const handleMSCQ_EnableAll = (hotspotID) => {
    const newHotspots = { ...hotspots };

    // Create new objects all the way down to ensure React detects the change
    newHotspots[hotspotID] = {
      ...newHotspots[hotspotID],
      props: {
        ...newHotspots[hotspotID].props,
        checkboxes: newHotspots[hotspotID].props.checkboxes.map((checkbox) => ({
          ...checkbox,
          disabled: false,
          selected: false,
        })),
      },
    };

    setHotspots(newHotspots);
  };

  const handleMSCQ_Reset = (hotspotID) => {
    const newHotspots = { ...hotspots };

    // Create new objects all the way down to ensure React detects the change
    newHotspots[hotspotID] = {
      ...newHotspots[hotspotID],
      props: {
        ...newHotspots[hotspotID].props,
        checkboxes: newHotspots[hotspotID].props.checkboxes.map((checkbox) => ({
          ...checkbox,
          disabled: false,
          selected: false,
        })),
        complete: false,
      },
    };

    setHotspots(newHotspots);
  };

  const fireTrigger = (triggerName) => {
    const updatedTriggerLog = triggerLog;
    updatedTriggerLog.push(triggerName);
    setTriggerLog([...updatedTriggerLog]);
  };

  const undoTrigger = (triggerName) => {
    if (triggerLog.includes(triggerName)) {
      let updatedTriggerLog = triggerLog;
      updatedTriggerLog = updatedTriggerLog.filter((e) => e !== triggerName);
      setTriggerLog([...updatedTriggerLog]);
    }
  };

  const canvasStyle = CSS.supports
    ? CSS.supports("aspect-ratio: 1/1")
      ? {
          aspectRatio: `${views[currentView].size.width}/${views[currentView].size.height}`,
        }
      : {
          // fallback for no aspect‑ratio support...
          width:
            window.innerWidth / window.innerHeight <
            views[currentView].size.width / views[currentView].size.height
              ? // portrait (relative to view): fill width, compute height from vw
                "100vw"
              : // landscape (relative to view): compute width from vh
                `${(views[currentView].size.width / views[currentView].size.height) * 100}vh`,
          height:
            window.innerWidth / window.innerHeight <
            views[currentView].size.width / views[currentView].size.height
              ? // portrait (relative to view): compute height from vw
                `${(views[currentView].size.height / views[currentView].size.width) * 100}vw`
              : // landscape (relative to view): fill height
                "100vh",
        }
    : {
        // if CSS.supports isn’t available, assume fallback
        width:
          window.innerWidth / window.innerHeight <
          views[currentView].size.width / views[currentView].size.height
            ? "100vw"
            : `${(views[currentView].size.width / views[currentView].size.height) * 100}vh`,
        height:
          window.innerWidth / window.innerHeight <
          views[currentView].size.width / views[currentView].size.height
            ? `${(views[currentView].size.height / views[currentView].size.width) * 100}vw`
            : "100vh",
      };

  return (
    <div
      ref={canvasContainerRef}
      className={className.canvasContainer}
      allow="fullscreen"
      allowFullScreen
      id={`canvas_${props.sceneData.id}`}
      style={{
        ...canvasStyle,
        aspectRatio: "auto",
      }}
    >
      <div
        ref={containerRef}
        className={className.canvas}
        style={{
          ...canvasStyle,
          overflow: devView ? "scroll" : "clip",
          maxHeight: props.standalone ? "100vh" : "100%",
        }}
        aria-label={
          !loaded
            ? FormattedMessage({
                string: "Loading",
              })
            : views[currentView].viewAltText !== ""
              ? FormattedMessage({
                  string: views[currentView].viewAltText,
                })
              : ""
        }
      >
        {loaded ? (
          <div
            className={className.gridContainer}
            style={{
              width: `calc(${(maxViewX / views[currentView].size.width) * 100 * Object.keys(views).length}%)`,
              height: `100%`,
              gridTemplateColumns: `repeat(${gridSize.x}, ${cellSize}px)`,
              gridTemplateRows: `repeat(${views[currentView].size.height}, ${cellSize}px)`,
              transform: devView
                ? null
                : `translate3d(
            -${(views[currentView].x_offset - 1) * cellSize}px,
            -${(views[currentView].y_offset - 1) * cellSize}px,
             0
          )`,
              willChange: "transform",
              transition: `${props.sceneData.transitionSpeed}s`,
            }}
          >
            {entities.map((entity, key) => {
              const state = checkEntityState(entity);

              if (state !== null) {
                if (entity.type === "image") {
                  return (
                    <GridItem
                      key={key}
                      type="entity"
                      gridColumnStart={entity.states[state].x_offset}
                      gridColumnEnd={
                        entity.states[state].x_offset +
                        entity.states[state].width
                      }
                      gridRowStart={entity.states[state].y_offset}
                      gridRowEnd={
                        entity.states[state].y_offset +
                        entity.states[state].height
                      }
                      show={false}
                      dev={dev && showGrid}
                      viewData={views[currentView]}
                    >
                      {preview ||
                      resources[
                        `${entity.states[state].props.src}_${intl.locale}`
                      ] !== undefined ||
                      resources[`${entity.states[state].props.src}`] !==
                        undefined ? (
                        <ImageEntity
                          state={state}
                          data={entity.states[state]}
                          src={
                            resources[
                              `${entity.states[state].props.src}_${intl.locale}`
                            ] || resources[`${entity.states[state].props.src}`]
                          }
                          allowOverflow={
                            entity.states[state].props.allowOverflow
                          }
                          preview={preview}
                        />
                      ) : null}
                    </GridItem>
                  );
                } else if (entity.type === "notebook") {
                  return (
                    <GridItem
                      key={key}
                      type="entity"
                      gridColumnStart={entity.states[state].x_offset}
                      gridColumnEnd={
                        entity.states[state].x_offset +
                        entity.states[state].width
                      }
                      gridRowStart={entity.states[state].y_offset}
                      gridRowEnd={
                        entity.states[state].y_offset +
                        entity.states[state].height
                      }
                      show={true}
                      dev={dev && showGrid}
                      viewData={views[currentView]}
                    >
                      <NotebookEntity
                        state={state}
                        data={entity.states[state]}
                        allowOverflow={entity.states[state].props.allowOverflow}
                        preview={preview}
                      />
                    </GridItem>
                  );
                } else return null;
              } else return null;
            })}
            {entities.map((entity, key) => {
              const state = checkEntityState(entity);

              if (entity.type === "animation") {
                const animId = entity.id || `anim-${key}`;
                const colorKey = `${animId}-${state}`;
                const isHidden = hiddenAnimations.has(colorKey);
                const isContentHidden = hiddenAnimationContent.has(colorKey);
                const isHighlighted =
                  showAnimationHighlight &&
                  state !== null &&
                  !isHidden &&
                  !isContentHidden;
                const highlightColor = isHighlighted
                  ? animationColorMap.get(colorKey)
                  : null;

                return (
                  <GridItem
                    key={key}
                    type="entity"
                    gridColumnStart={entity.x_offset}
                    gridColumnEnd={entity.x_offset + entity.width}
                    gridRowStart={entity.y_offset}
                    gridRowEnd={entity.y_offset + entity.height}
                    show={false}
                    dev={dev && showGrid}
                    viewData={views[currentView]}
                    style={
                      isHighlighted
                        ? {
                            border: `3px solid ${highlightColor}`,
                            boxShadow: `0 0 15px ${highlightColor}`,
                            position: "relative",
                          }
                        : {}
                    }
                  >
                    {isHighlighted && (
                      <>
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            backgroundColor:
                              highlightColor || "rgba(255, 0, 0, 0.1)",
                            opacity: 0.1,
                            pointerEvents: "none",
                            zIndex: 9999,
                          }}
                        />
                        <div
                          style={{
                            position: "absolute",
                            top: "4px",
                            left: "4px",
                            backgroundColor: highlightColor || "#999",
                            color: "#fff",
                            padding: "2px 6px",
                            fontSize: "11px",
                            fontWeight: "bold",
                            borderRadius: "3px",
                            pointerEvents: "none",
                            zIndex: 10000,
                            fontFamily: "sans-serif",
                          }}
                        >
                          Anim {animId}
                        </div>
                      </>
                    )}
                    <div style={{ opacity: isContentHidden ? 0 : 1 }}>
                      <AnimationEntity
                        hidden={state === null}
                        state={state || entity.states[0]}
                        data={entity.states[state] || entity.states[0]}
                        src={entity.props.src}
                        fireTrigger={fireTrigger}
                        undoTrigger={undoTrigger}
                        previewSrc={entity.props.url}
                        allowOverflow={entity.props.allowOverflow}
                        playedStates={playedStates.current}
                        preview={preview}
                      />
                    </div>
                  </GridItem>
                );
              } else return null;
            })}
            {showGrid ? (
              <div
                className={className.gridLines}
                style={{
                  gridTemplateColumns: `repeat(${gridSize.x}, ${cellSize}px)`,
                  gridTemplateRows: `repeat(${views[currentView].size.height}, ${cellSize}px)`,
                }}
              >
                {grid.map((cell, key) => {
                  let redundant = false;
                  Object.keys(hotspots).map((hotspotID) => {
                    const hotspot = hotspots[hotspotID];
                    if (
                      checkHotspotVisibilityDependencies(hotspot) &&
                      checkHotspotHiddenDependencies(hotspot, hotspotID)
                    ) {
                      if (
                        cell.y >= hotspot.y_offset &&
                        cell.y <= hotspot.y_offset + hotspot.height - 1
                      ) {
                        if (
                          cell.x >= hotspot.x_offset &&
                          cell.x <= hotspot.x_offset + hotspot.width - 1
                        ) {
                          redundant = true;
                        }
                      }
                    }
                  });
                  return (
                    <GridItem
                      key={key}
                      type={redundant && showHotspots ? "hidden" : "cell"}
                      gridColumnStart={cell.x}
                      gridColumnEnd={cell.x + 1}
                      gridRowStart={cell.y}
                      gridRowEnd={cell.y + 1}
                      show={showGrid}
                      dev={dev && showGrid}
                      viewData={views[currentView]}
                    ></GridItem>
                  );
                })}
              </div>
            ) : null}
            {hotspotOrder.map((hotspotID) => {
              const hotspot = hotspots[hotspotID];
              if (!hotspot) return null; // Prevent errors if a hotspot is missing

              if (!hotspot.triggered) {
                checkCompleteDependencies(hotspotID);
              }
              const visible =
                checkHotspotVisibilityDependencies(hotspot) &&
                checkHotspotHiddenDependencies(hotspot);
              const locked = checkHotspotLockedDependencies(hotspot);

              const isCorrect = props.sceneData.hotspots[hotspotID]?.isCorrect;
              if (props.sceneData.hotspots[hotspotID].props.image) {
                hotspot.props.image =
                  props.sceneData.hotspots[hotspotID].props.image;
              }
              if (visible) {
                return (
                  <GridItem
                    key={hotspotID}
                    type="hotspot"
                    gridColumnStart={hotspot.x_offset}
                    gridColumnEnd={hotspot.x_offset + hotspot.width}
                    gridRowStart={hotspot.y_offset}
                    gridRowEnd={hotspot.y_offset + hotspot.height}
                    show={showGrid && showHotspots}
                    dev={dev && showGrid}
                    viewData={views[currentView]}
                  >
                    <Hotspot
                      id={hotspotID}
                      selectedHotspot={props.selectedHotspot}
                      setLatestHotspotRef={(ref) => setLatestHotspotRef(ref)}
                      data={hotspot}
                      changeView={changeView}
                      changeState={changeState}
                      handleTrigger={handleTrigger}
                      updateWidgetData={updateWidgetData}
                      locked={locked}
                      hide={!showHotspots}
                      showGrid={showGrid}
                      currentView={currentView}
                      idle={
                        !views[currentView].complete &&
                        tick >= 2 &&
                        tick % 2 === 0
                      }
                      device={device}
                      cellSize={cellSize}
                      viewData={views[currentView]}
                      transitionSpeed={props.sceneData.transitionSpeed}
                      isDiabledBlurOnSmall={props.isDiabledBlurOnSmall}
                      isCorrect={isCorrect}
                      usersnapProjectId={props.sceneData.usersnap}
                    />
                  </GridItem>
                );
              }
              return null;
            })}
          </div>
        ) : (
          <div className={className["fallback-loading"]} aria-hidden={true}>
            <div>.</div>
            <div>.</div>
            <div>.</div>
          </div>
        )}
      </div>
      {views[currentView].backToView !== null && (
        <Button
          className={`${className.viewBackBtn} ${"tertiary-button md"} ${currentView === props.sceneData.mainView ? className.hidden : ""}`}
          tabIndex={0}
          onClick={() => {
            setCurrentView(views[currentView].backToView);
          }}
          titleDescriptor={{
            id: "Back",
            defaultMessage: "Back",
            description: "Go back to the previous view.",
          }}
          iconName="chevron-left"
        />
      )}
      {widgetData.active || closeWidget ? (
        <WidgetWrapper
          type={widgetData.type}
          hotspotID={widgetData.hotspotID}
          handleTrigger={handleTrigger}
          closeWidget={closeWidget}
          widgetProps={hotspots[widgetData.hotspotID].props}
          handleCloseWidget={handleCloseWidget}
          handleCSCQ_OptionSelect={handleCSCQ_OptionSelect}
          handleCSCQ_Submit={handleCSCQ_Submit}
          handleTableQuestion_Submit={handleTableQuestion_Submit}
          handleMSCQ_OptionSelect={handleMSCQ_OptionSelect}
          handleMSCQ_Submit={handleMSCQ_Submit}
          handleMSCQ_EnableAll={handleMSCQ_EnableAll}
          handleMSCQ_Reset={handleMSCQ_Reset}
          handleMSCQRedux_OptionSelect={handleMSCQRedux_OptionSelect}
          handleMSCQRedux_Submit={handleMSCQRedux_Submit}
          preview={preview}
        />
      ) : null}
      {dev && preview && (
        <DevMenu
          showGrid={showGrid}
          setShowGrid={setShowGrid}
          devView={devView}
          setDevView={setDevView}
          showAnimationHighlight={showAnimationHighlight}
          setShowAnimationHighlight={setShowAnimationHighlight}
        />
      )}
      {dev && preview && showAnimationHighlight && (
        <AnimationLegend
          activeAnimations={activeAnimations}
          colorMap={animationColorMap}
          hiddenAnimations={hiddenAnimations}
          hiddenAnimationContent={hiddenAnimationContent}
          onToggleHighlight={toggleAnimationHighlight}
          onToggleContent={toggleAnimationContent}
        />
      )}
      {introModal && (
        <IntroModal
          {...(typeof props.sceneData.intro === "object"
            ? props.sceneData.intro
            : { text: props.sceneData.intro })}
          closeIntroModal={() => setIntroModal(false)}
        />
      )}
      {showOrientationPrompt && (
        <RotateOverlay
          setOrientationPromptDismissed={setOrientationPromptDismissed}
          setShowOrientationPrompt={setShowOrientationPrompt}
        />
      )}
    </div>
  );
}

ImageDiscoveryCanvas.propTypes = {
  sceneData: PropTypes.shape({
    mainView: PropTypes.number.isRequired,
    views: PropTypes.object.isRequired,
    hotspots: PropTypes.object.isRequired,
    entities: PropTypes.array.isRequired,
    transitionSpeed: PropTypes.number.isRequired,
  }).isRequired,
  standalone: PropTypes.bool,
  updateProgress: PropTypes.func,
  trackProgress: PropTypes.bool,
  isDiabledBlurOnSmall: PropTypes.bool,
  selectedHotspot: PropTypes.number,
  componentID: PropTypes.number,
  requiresRedux: PropTypes.bool,
  preview: PropTypes.bool,
};

ImageDiscoveryCanvas.defaultProps = {
  standalone: false,
  trackProgress: false,
  isDiabledBlurOnSmall: false,
  selectedHotspot: null,
  componentID: null,
  requiresRedux: false,
};
